import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useLanguage } from '../../i18n/LanguageContext';
import { useDealer } from '../../context/DealerContext';
import { dealerApi, DealerBooking, RescheduleOptions } from '../../services/dealerApi';
import Money from '../../components/dealer/Money';
import StatusChip from '../../components/dealer/StatusChip';
import { dealerErrorMessage, formatDate } from '../../utils/dealer';

const CLOSED = new Set(['CANCELLED', 'EXPIRED', 'REJECTED', 'PAYMENT_FAILED']);

/**
 * Spec 085 (Flow H step 5, Flow J, BR-20) — one booking this dealer made, with the order timeline.
 * The dealer may cancel or reschedule it while the order is before driver assignment / IN_PROGRESS;
 * the backend decides (409 NOT_ALLOWED_IN_STATE). A booking stuck in PENDING_PAYMENT can retry the
 * credit settlement (idempotent).
 */
export default function DealerBookingDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { t, lang } = useLanguage();
  const { token } = useDealer();
  const [booking, setBooking] = useState<DealerBooking | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [resched, setResched] = useState<RescheduleOptions | null>(null);
  const [reschedDate, setReschedDate] = useState('');
  const [reschedPickup, setReschedPickup] = useState('');
  const [reschedReturn, setReschedReturn] = useState('');

  const load = useCallback(async () => {
    if (!token || !id) return;
    try {
      setBooking(await dealerApi.getBooking(id, token));
      setError(null);
    } catch (err) {
      setError(dealerErrorMessage(err, t));
    }
  }, [token, id, t]);

  useEffect(() => { load(); }, [load]);

  const act = async (fn: () => Promise<DealerBooking>, success: string) => {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      setBooking(await fn());
      setNotice(success);
      await load();
    } catch (err) {
      setError(dealerErrorMessage(err, t));
    } finally {
      setBusy(false);
    }
  };

  const openReschedule = async (date?: string) => {
    if (!token || !id) return;
    setError(null);
    try {
      const opts = await dealerApi.rescheduleOptions(id, token, date);
      setResched(opts);
      setReschedDate(date ?? opts.current?.date ?? '');
      setReschedPickup(opts.current?.pickupTimeSlotId ?? '');
      setReschedReturn(opts.current?.returnTimeSlotId ?? '');
    } catch (err) {
      setError(dealerErrorMessage(err, t));
    }
  };

  if (!booking) {
    return (
      <div className="dealer-page">
        {error ? <div className="error-message">{error}</div> : <p className="dealer-muted">{t('dealer.common.loading')}</p>}
      </div>
    );
  }

  const open = !CLOSED.has(booking.status) && booking.orderStatus !== 'CANCELLED' && booking.orderStatus !== 'COMPLETED';

  return (
    <div className="dealer-page">
      <Link to="/dealer/bookings" className="dealer-back">{t('dealer.common.backToList')}</Link>
      <div className="dealer-page__header">
        <div>
          <h2>{booking.serviceName || t('dealer.bookings.booking')}</h2>
          <p className="dealer-muted">
            {booking.garageName} · {formatDate(booking.scheduledDate, lang)}
            {booking.orderNumber && <> · <span dir="ltr">{booking.orderNumber}</span></>}
          </p>
        </div>
        <StatusChip status={booking.orderStatus ?? booking.status} />
      </div>

      {notice && <div className="success-message" role="status">{notice}</div>}
      {error && <div className="error-message" role="alert">{error}</div>}

      <div className="dealer-card">
        <dl className="dealer-dl">
          <dt>{t('dealer.wizard.total')}</dt><dd><Money value={booking.totalPayable} /></dd>
          <dt>{t('dealer.wizard.creditApplied')}</dt><dd><Money value={booking.creditAppliedAed} /></dd>
          <dt>{t('dealer.wizard.toPayNow')}</dt><dd><Money value={booking.toPayNowAed} /></dd>
        </dl>
        {booking.status === 'PENDING_PAYMENT' && (
          <div className="dealer-note dealer-note--warn">
            <p>{t('dealer.bookingDetail.pendingPayment')}</p>
            <button type="button" className="btn btn-primary btn-sm" disabled={busy}
              onClick={() => act(() => dealerApi.completeWithCredit(booking.bookingId, token!), t('dealer.bookingDetail.settled'))}>
              {t('dealer.bookingDetail.retrySettlement')}
            </button>
          </div>
        )}
        {open && (
          <div className="dealer-actions">
            {booking.orderId && (
              <button type="button" className="btn btn-secondary" disabled={busy} onClick={() => openReschedule()}>
                {t('dealer.bookingDetail.reschedule')}
              </button>
            )}
            <button type="button" className="btn btn-danger" disabled={busy} onClick={() => setConfirmCancel(true)}>
              {t('dealer.bookingDetail.cancel')}
            </button>
          </div>
        )}
      </div>

      {resched && (
        <div className="dealer-card">
          <h3>{t('dealer.bookingDetail.reschedule')}</h3>
          {!resched.allowed ? (
            <p className="dealer-note dealer-note--warn">{resched.reasonMessage || t('dealer.error.NOT_ALLOWED_IN_STATE')}</p>
          ) : (
            <>
              <div className="dealer-grid-2">
                <div className="form-group">
                  <label htmlFor="resched-date">{t('dealer.bookings.date')}</label>
                  <input id="resched-date" type="date" className="dealer-input" value={reschedDate}
                    onChange={(e) => { setReschedDate(e.target.value); openReschedule(e.target.value); }} />
                </div>
                <div className="form-group">
                  <label htmlFor="resched-pickup">{t('dealer.wizard.pickupSlot')}</label>
                  <select id="resched-pickup" className="dealer-select" value={reschedPickup} onChange={(e) => setReschedPickup(e.target.value)}>
                    <option value="">{t('dealer.wizard.chooseSlot')}</option>
                    {(resched.slots ?? []).map((sl) => <option key={sl.id} value={sl.id}>{sl.label}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label htmlFor="resched-return">{t('dealer.wizard.returnSlot')}</label>
                  <select id="resched-return" className="dealer-select" value={reschedReturn} onChange={(e) => setReschedReturn(e.target.value)}>
                    <option value="">{t('dealer.wizard.anySlot')}</option>
                    {(resched.slots ?? []).map((sl) => <option key={sl.id} value={sl.id}>{sl.label}</option>)}
                  </select>
                </div>
              </div>
              {resched.capacity?.full && <p className="dealer-note dealer-note--warn">{t('dealer.error.CAPACITY_CONFIRM_REQUIRED')}</p>}
              <div className="dealer-actions">
                <button type="button" className="btn btn-primary"
                  disabled={busy || !reschedDate || booking.orderVersion == null || !!resched.capacity?.full}
                  onClick={() => act(() => dealerApi.reschedule(booking.bookingId, {
                    date: reschedDate,
                    pickupTimeSlotId: reschedPickup || undefined,
                    returnTimeSlotId: reschedReturn || undefined,
                    expectedVersion: booking.orderVersion!,
                  }, token!), t('dealer.bookingDetail.rescheduled')).then(() => setResched(null))}>
                  {t('dealer.bookingDetail.saveReschedule')}
                </button>
                <button type="button" className="btn btn-secondary" onClick={() => setResched(null)}>{t('common.cancel')}</button>
              </div>
            </>
          )}
        </div>
      )}

      <div className="dealer-card">
        <h3>{t('dealer.bookingDetail.timeline')}</h3>
        {!booking.timeline || booking.timeline.length === 0 ? (
          <p className="dealer-muted">{t('dealer.bookingDetail.noTimeline')}</p>
        ) : (
          <ol className="dealer-timeline">
            {booking.timeline.map((e, i) => {
              const key = `dealer.timeline.${e.type}`;
              return (
                <li key={`${e.type}-${i}`}>
                  <span className="dealer-timeline__type">{t(key) === key ? e.type : t(key)}</span>
                  <span className="dealer-muted">{e.at ? new Date(e.at).toLocaleString(lang === 'ar' ? 'ar-AE' : 'en-GB') : ''}</span>
                </li>
              );
            })}
          </ol>
        )}
      </div>

      {confirmCancel && (
        <div className="modal-overlay" role="dialog" aria-modal="true" aria-labelledby="cancel-title">
          <div className="modal-content dealer-dialog">
            <h3 id="cancel-title">{t('dealer.bookingDetail.cancelTitle')}</h3>
            <p>{t('dealer.bookingDetail.cancelBody')}</p>
            <div className="dealer-actions dealer-actions--end">
              <button type="button" className="btn btn-secondary" onClick={() => setConfirmCancel(false)}>{t('dealer.common.keep')}</button>
              <button type="button" className="btn btn-danger" disabled={busy} onClick={() => {
                setConfirmCancel(false);
                act(() => dealerApi.cancelBooking(booking.bookingId, token!), t('dealer.bookingDetail.cancelled'));
              }}>{t('dealer.bookingDetail.cancel')}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
